interface Assertions {
  /**
   * Throws an error if a value-optional type is undefined, and narrows
   * the type if not. Useful for declaring invariants
   *
   * @param thing - The thing you are trying to narrow
   * @param name - An optional message to pass into the error
   */
  defined: <T>(thing: T | undefined, message?: string) => asserts thing is T

  /**
   * In JavaScript, its entirely possible to 'throw' something that isn't an error object.
   * `useUnknownInCatchVariables` acknowledges this by forcing you to check the type of
   * the error before you use it.
   *
   * This function takes 'something' that was caught by a catch block. If its not an 'Error' object
   * it'll just re-throw it. If it doesn't re-throw the type will be narrowed so you can safely access
   * the `stack` or `message` even with `useUnknownInCatchVariables` turned on
   *
   * Why re-throw it?
   *
   * If upstream code is throwing things that are not errors, its **strange** behaviour and you usually want
   * to know about it straight away - if this gets into your o11y setup, you won't have any stack traces
   * which will make debugging tonnes harder
   *
   * @param thing - The thing that might be an error
   */
  error: (thing: unknown) => asserts thing is Error
}

export const Assert: Assertions = {
  error: (thing) => {
    if (!(thing instanceof Error)) {
      throw new Error('Caught error that was not an Error object', {
        cause: thing,
      })
    }
  },
  defined: (thing, message) => {
    if (typeof thing === 'undefined') {
      throw new Error(message ?? `Expected 'thing' to be truthy`)
    }
  },
}
